// src/components/ActivityFormModal.js
import React, { useEffect, useMemo, useState } from "react";
import { api } from "api";
import ActivityFormFields from "./ActivityFormFields";
import { Preferences } from "@capacitor/preferences"; // iOS-safe key/value

const toAbs = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = api.defaults?.baseURL?.replace(/\/+$/, "") || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
};

// ---- Tiny token presence check (interceptor will attach it if present)
const hasToken = async () => {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return true;
  } catch {}
  if (typeof window !== "undefined") {
    if (localStorage.getItem("token") || sessionStorage.getItem("token")) return true;
  }
  return false;
};

// ---- Resolve/cached user id (Preferences -> localStorage -> /users/me)
const getUserId = async () => {
  try {
    const r = await Preferences.get({ key: "user_id" });
    if (r?.value) return Number(r.value);
  } catch {}
  const v = (typeof window !== "undefined") ? localStorage.getItem("user_id") : null;
  if (v) return Number(v);

  try {
    const { data } = await api.get("/users/me");
    if (data?.id) {
      try { await Preferences.set({ key: "user_id", value: String(data.id) }); } catch {}
      if (typeof window !== "undefined") {
        localStorage.setItem("user_id", String(data.id));
      }
      return Number(data.id);
    }
  } catch {
    /* ignore */
  }
  return null;
};

const MAX_MEDIA = 5;

const getLocalDatetimeValue = () => {
  const date = new Date();
  const pad = (value) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function ActivityFormModal({
  isOpen,
  onClose,
  mode = "create",
  initial,
  circleId = null,
  fetchFeed,
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    tags: "",
    mood: "",
    duration: "",
    location: "",
    timestamp: "",
    media_file: null,
    media_files: [],
    circle_ids: [],
    visibility: "",      // current visibility for this activity
    base_visibility: "", // original non-private visibility (for toggling back)
  });

  const [joinedCircles, setJoinedCircles] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Lock scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevTouch = body.style.touchAction;
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    return () => {
      body.style.overflow = prevOverflow;
      body.style.touchAction = prevTouch;
    };
  }, [isOpen]);

  // Seed form when opening
  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && initial) {
      const seededCircles = Array.isArray(initial.circles)
        ? initial.circles.map((g) => Number(g.id)).filter(Number.isFinite)
        : (Number.isFinite(Number(initial.circle_id)) ? [Number(initial.circle_id)] : []);
      
      const isPrivate = initial.visibility === "private" || initial.is_private === true;

      setForm({
        title: initial.title || "",
        description: initial.description || "",
        tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
        mood: initial.mood || "",
        duration: initial.duration || "",
        location: initial.location || "",
        timestamp: initial.timestamp ? new Date(initial.timestamp).toISOString().slice(0, 16) : "",
        media_file: null,
        media_files: [],
        circle_ids: seededCircles,
        visibility: isPrivate ? "private" : (initial.visibility || ""),
        base_visibility: initial.base_visibility || initial.visibility || "",
      });
      setPreviewUrl(null);
      setError("");
    }

    if (mode === "create") {
      setForm({
        title: "",
        description: "",
        tags: "",
        mood: "",
        duration: "",
        location: "",
        timestamp: getLocalDatetimeValue(),
        media_file: null,
        media_files: [],
        circle_ids: circleId ? [Number(circleId)] : [],
        visibility: "",
        base_visibility: "",
      });

      setPreviewUrl(null);
      setError("");
    }
  }, [mode, initial, isOpen, circleId]);

  // If editing and we didn't receive circles embedded, hydrate them
  useEffect(() => {
    if (!isOpen || mode !== "edit" || !initial?.id) return;
    if (Array.isArray(form.circle_ids) && form.circle_ids.length) return;

    let cancelled = false;
    (async () => {
      try {
        const uid = await getUserId();
        if (!uid) return;
        const { data } = await api.get(`/activities/${initial.id}/circles`);
        const ids = Array.isArray(data)
          ? data.map((g) => Number(g.id)).filter(Number.isFinite)
          : [];
        if (!cancelled && ids.length) {
          setForm((f) => ({ ...f, circle_ids: ids }));
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, initial?.id]);

  // Load user's joined circles for the picker
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const uid = await getUserId();
        if (!uid) return;
        const r = await api.get(`/users/${uid}/groups`);
        setJoinedCircles(r.data || []);
      } catch {
        setJoinedCircles([]);
      }
    })();
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey, { passive: true });
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Current media (single) for legacy edit display
  const currentMedia = useMemo(
    () => (mode === "edit" && initial?.media_url ? toAbs(initial.media_url) : null),
    [mode, initial]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    // Light pre-check for UX; server will still 401 if truly unauthenticated.
    if (!(await hasToken())) {
      setError("Not authenticated");
      setSaving(false);
      return;
    }

    const fd = new FormData();

    fd.append("title", String(form.title));
    if (form.description) fd.append("description", String(form.description));
    if (form.mood) fd.append("mood", String(form.mood));
    if (form.duration) fd.append("duration", String(form.duration));
    if (form.location) fd.append("location", String(form.location));
    // Activity-level visibility:
    // - On create: only send if explicitly private (server uses user default otherwise)
    // - On edit: always send explicit value so toggling is honored
    if (mode === "edit") {
      if (form.visibility) {
        fd.append("visibility", form.visibility);
      }
    } else {
      if (form.visibility === "private") {
        fd.append("visibility", "private");
      }
    }

    if (form.timestamp) {
      fd.append("timestamp", new Date(form.timestamp).toISOString());
    }

    // Always send tags (even empty) so they can be cleared
    const tagList = (form.tags || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    fd.append("tags", JSON.stringify(tagList));

    // Always send circle_ids (even empty []) so backend can clear
    const circleIdsPayload = (circleId ? [Number(circleId)] : (form.circle_ids || []))
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n));
    fd.append("circle_ids", JSON.stringify(circleIdsPayload));

    // Multi-file support (back-compat with single)
    const files = Array.isArray(form.media_files) && form.media_files.length
      ? form.media_files
      : (form.media_file ? [form.media_file] : []);
    const limited = files.slice(0, MAX_MEDIA);

    if (limited.length === 1) {
      fd.append("media_file", limited[0]);
    } else if (limited.length > 1) {
      limited.forEach((f) => fd.append("media_files", f));
      fd.append("media_count", String(limited.length));
    }

    try {
      if (mode === "create") {
        await api.post("/activities/upload", fd);
      } else {
        await api.put(`/activities/${initial.id}`, fd);
      }
      onClose?.();
      fetchFeed?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail.map((d) => `${d.loc?.join(".")}: ${d.msg}`).join(" • ")
          : detail || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      role="dialog"
      aria-modal="true"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div
        className="w-full sm:max-w-lg md:max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden"
        style={{ maxHeight: "90svh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b px-5 py-3 flex items-center justify-between">
          <h2 className="text-base sm:text-lg font-semibold">
            {mode === "create" ? "Upload Activity" : "Edit Activity"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 px-2 py-1"
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-5 sm:px-6 md:px-7 py-5 overflow-y-auto space-y-4"
          style={{
            maxHeight: "calc(90svh - 52px)",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <ActivityFormFields
            form={form}
            setForm={setForm}
            circleId={circleId}
            joinedCircles={joinedCircles}
            showCircles={!circleId}
            showTimestamp={true}
            previewUrl={previewUrl}
            setPreviewUrl={setPreviewUrl}
            currentMedia={currentMedia}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
              style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}
            >
              {saving ? "Saving…" : mode === "create" ? "Submit" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
